import { useLayoutEffect, useCallback } from 'react';

export default function useCustomTheme(css: string, themeId: string, brandColor?: string, faviconUrl?: string | null) {
  const injectRawCss = useCallback(
    (css: string) => {
      // Remove existing custom theme styles to prevent accumulation
      const existingStyle = document.getElementById(themeId);
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.id = themeId;
      style.textContent = css;
      document.head.insertBefore(style, document.head.firstChild);
    },
    [themeId]
  );

  const updateFavicon = useCallback(
    (faviconUrl: string | null) => {
      const faviconId = `${themeId}-favicon`;

      // Remove ALL existing favicon links to prevent conflicts
      const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
      if (existingFavicons.length > 0) {
        existingFavicons.forEach((favicon) => favicon.remove());
      }

      if (faviconUrl) {
        const link = document.createElement('link');
        link.id = faviconId;
        link.rel = 'icon';
        link.type = 'image/png';
        link.href = faviconUrl;
        document.head.insertBefore(link, document.head.firstChild);
      }
    },
    [themeId]
  );

  const updateThemeColor = useCallback(
    (brandColor: string | null) => {
      const themeColorId = `${themeId}-theme-color`;

      // Remove existing theme-color meta tags
      const existingThemeColor = document.querySelectorAll('meta[name="theme-color"]');
      if (existingThemeColor.length > 0) {
        existingThemeColor.forEach((meta) => meta.remove());
      }

      if (brandColor) {
        const meta = document.createElement('meta');
        meta.id = themeColorId;
        meta.name = 'theme-color';
        meta.content = brandColor;
        document.head.insertBefore(meta, document.head.firstChild);
      }
    },
    [themeId]
  );

  useLayoutEffect(() => {
    if (!!css) {
      injectRawCss(css);
    }

    if (faviconUrl !== undefined) {
      updateFavicon(faviconUrl);
    }

    if (brandColor) {
      updateThemeColor(brandColor);
    }

    return () => {
      const existingStyle = document.getElementById(themeId);
      if (existingStyle) {
        existingStyle.remove();
      }

      const existingFavicon = document.getElementById(`${themeId}-favicon`);
      if (existingFavicon) {
        existingFavicon.remove();
      }

      const existingThemeColor = document.getElementById(`${themeId}-theme-color`);
      if (existingThemeColor) {
        existingThemeColor.remove();
      }
    };
  }, [css, faviconUrl, brandColor, injectRawCss, updateFavicon, updateThemeColor, themeId]);
}
